export interface VillaModel {
  id: number;
  slug: string;
  type: string;
  name: string;
  price: string;
  priceLabel: string;
  roi: string;
  tag: string;
  sqft: string;
  bedrooms: string;
  guests: string;
  featured: string[];
  features: string[];
  gallery: string[];
  description: string;
  longDescription: string;
}

const INTERIOR_A =
  "https://readdy.ai/api/search-image?query=Interior%20of%20a%20premium%20A-frame%20wooden%20villa%2C%20open%20living%20room%20with%20floor-to-ceiling%20triangular%20glass%20window%20overlooking%20a%20misty%20green%20valley%2C%20warm%20natural%20light%2C%20modern%20minimalist%20wood%20furniture%2C%20cozy%20luxury%20cabin%20interior%2C%20editorial%20hospitality%20photography&width=900&height=600&seq=villa-interior-aframe-01&orientation=landscape";

const INTERIOR_B =
  "https://readdy.ai/api/search-image?query=Cozy%20loft%20bedroom%20in%20an%20A-frame%20cabin%20villa%2C%20skylight%20and%20wooden%20ceiling%2C%20soft%20warm%20bedding%2C%20forest%20view%20through%20large%20window%2C%20calm%20morning%20light%2C%20luxury%20nature%20retreat%20interior%2C%20editorial%20photography&width=900&height=600&seq=villa-interior-aframe-02&orientation=landscape";

export const VILLA_MODELS: VillaModel[] = [
  {
    id: 1,
    slug: "nomad-one",
    type: "Compact Cabin",
    name: "Nomad One",
    price: "52 Lakhs",
    priceLabel: "₹52 Lakhs",
    roi: "26%",
    tag: "Entry",
    sqft: "480 sq.ft",
    bedrooms: "1 Bed",
    guests: "2 Guests",
    featured: ["Lightweight single-cabin A-frame", "Ideal starter investment", "Perfect for Airbnb short stays"],
    features: [
      "480 sq.ft compact single-cabin layout",
      "Floor-to-ceiling triangular glass front",
      "Ideal starter investment",
      "Perfect for Airbnb short stays",
    ],
    gallery: [
      "https://readdy.ai/api/search-image?query=Compact%20lightweight%20single-cabin%20A-frame%20wooden%20villa%20in%20a%20pine%20forest%20clearing%2C%20minimal%20starter%20cabin%20design%2C%20warm%20evening%20light%2C%20stylized%20artistic%20render%2C%20earthy%20green%20and%20amber%20tones%2C%20high%20detail&width=1200&height=800&seq=villa-nomad-one-01&orientation=landscape",
      INTERIOR_A,
      INTERIOR_B,
      "https://readdy.ai/api/search-image?query=Small%20A-frame%20cabin%20villa%20at%20dusk%20with%20warm%20glowing%20windows%20and%20a%20small%20wooden%20deck%2C%20pine%20forest%20backdrop%2C%20cozy%20starter%20investment%20cabin%2C%20stylized%20artistic%20render%2C%20warm%20tones%2C%20high%20detail&width=1200&height=800&seq=villa-nomad-one-ext&orientation=landscape",
    ],
    description:
      "Our most accessible entry point. Nomad One is a compact, self-contained A-frame cabin built for the thriving short-stay market — a smart, low-cost way to begin your villa investment journey.",
    longDescription:
      "Nomad One is designed for first-time hospitality investors who want a lean, low-risk entry into villa ownership. Its single-cabin layout keeps build cost and maintenance minimal, while the signature glass front and cosy interior still command attractive nightly rates on Airbnb and OTA platforms. Professionally managed end-to-end, it delivers consistent occupancy with the smallest capital outlay in our portfolio.",
  },
  {
    id: 2,
    slug: "aurora",
    type: "Studio A-Frame",
    name: "Aurora",
    price: "66 Lakhs",
    priceLabel: "₹66 Lakhs",
    roi: "28%",
    tag: "Popular",
    sqft: "720 sq.ft",
    bedrooms: "1 Bed",
    guests: "3 Guests",
    featured: ["Signature glass-front studio", "Scenic viewing deck", "High-demand staycation unit"],
    features: [
      "720 sq.ft signature glass-front studio",
      "Private scenic viewing deck",
      "High-demand staycation unit",
      "Premium fit-out and smart controls",
    ],
    gallery: [
      "https://readdy.ai/api/search-image?query=Signature%20glass-front%20studio%20A-frame%20villa%20with%20a%20scenic%20wooden%20viewing%20deck%20overlooking%20a%20misty%20green%20valley%2C%20warm%20glowing%20interior%2C%20dusk%20light%2C%20stylized%20artistic%20render%2C%20high%20detail&width=1200&height=800&seq=villa-aurora-02&orientation=landscape",
      INTERIOR_A,
      INTERIOR_B,
      "https://readdy.ai/api/search-image?query=Modern%20glass-front%20studio%20A-frame%20villa%20at%20golden%20hour%20with%20panoramic%20deck%20and%20lounge%20chairs%2C%20warm%20interior%20glow%2C%20mountain%20valley%20backdrop%2C%20stylized%20artistic%20render%2C%20high%20detail&width=1200&height=800&seq=villa-aurora-ext&orientation=landscape",
    ],
    description:
      "Our best-selling studio. Aurora pairs a striking glass-front A-frame with a private viewing deck, making it the most-booked staycation unit for couples and small groups.",
    longDescription:
      "Aurora is the sweet spot between affordability and wow-factor. The signature floor-to-ceiling glass front floods the studio with light and frames breathtaking valley views, while the private wooden deck invites guests to linger longer — driving higher nightly rates and repeat bookings. As our most popular model, it enjoys strong year-round demand from weekenders, couples and boutique-stay travellers.",
  },
  {
    id: 3,
    slug: "havenside",
    type: "2 BHK Family",
    name: "Havenside",
    price: "98 Lakhs",
    priceLabel: "₹98 Lakhs",
    roi: "32%",
    tag: "Premium",
    sqft: "1180 sq.ft",
    bedrooms: "2 Bed",
    guests: "5 Guests",
    featured: ["Spacious family retreat", "Rents beautifully all year", "Doubles as a holiday home"],
    features: [
      "1180 sq.ft spacious two-bedroom layout",
      "Separate living and dining zones",
      "Rents beautifully all year round",
      "Doubles as a private family holiday home",
    ],
    gallery: [
      "https://readdy.ai/api/search-image?query=Spacious%20family%20A-frame%20villa%20retreat%20with%20warm%20wooden%20exterior%20and%20large%20terrace%2C%20lush%20green%20garden%20surroundings%2C%20bright%20daylight%2C%20stylized%20artistic%20render%2C%20warm%20green%20and%20gold%20tones%2C%20high%20detail&width=1200&height=800&seq=villa-havenside-03&orientation=landscape",
      INTERIOR_A,
      INTERIOR_B,
      "https://readdy.ai/api/search-image?query=Large%20family%20A-frame%20cabin%20villa%20with%20wrap-around%20wooden%20terrace%20and%20landscaped%20lawn%2C%20bright%20sunny%20day%2C%20warm%20wood%20and%20green%20tones%2C%20stylized%20artistic%20render%2C%20high%20detail&width=1200&height=800&seq=villa-havenside-ext&orientation=landscape",
    ],
    description:
      "A spacious family retreat that rents beautifully all year. Havenside offers room to grow, plenty of comfort, and the flexibility to double as your own holiday home.",
    longDescription:
      "Havenside is built for families and groups who want space without compromising on the A-frame aesthetic. Two generous bedrooms, a proper living area and outdoor terrace make it a standout on rental platforms while remaining a cosy second home for you. It captures the fast-growing family-stay and multi-night booking segment, delivering a higher ROI from larger nightly tariffs and longer stays.",
  },
  {
    id: 4,
    slug: "slopeview",
    type: "Premium Glass",
    name: "Slopeview",
    price: "1.2 Cr",
    priceLabel: "₹1.2 Cr",
    roi: "34%",
    tag: "Flagship",
    sqft: "1500 sq.ft",
    bedrooms: "2 Bed",
    guests: "6 Guests",
    featured: ["Floor-to-ceiling valley views", "Premium nightly rates", "Top revenue generator"],
    features: [
      "1500 sq.ft flagship glass pavilion",
      "Floor-to-ceiling valley views",
      "Premium nightly rates",
      "Top revenue generator in the portfolio",
    ],
    gallery: [
      "https://readdy.ai/api/search-image?query=Luxury%20premium%20glass%20A-frame%20villa%20with%20floor-to-ceiling%20windows%20overlooking%20a%20wide%20green%20valley%20at%20golden%20hour%2C%20elevated%20hillside%20setting%2C%20stylized%20artistic%20render%2C%20warm%20amber%20tones%2C%20high%20detail&width=1200&height=800&seq=villa-slopeview-04&orientation=landscape",
      INTERIOR_A,
      INTERIOR_B,
      "https://readdy.ai/api/search-image?query=Ultra-luxury%20glass%20A-frame%20villa%20on%20an%20elevated%20hillside%20with%20panoramic%20infinity%20views%2C%20infinity%20edge%20deck%2C%20golden%20hour%20glow%2C%20stylized%20artistic%20render%2C%20premium%20real%20estate%20photography%2C%20high%20detail&width=1200&height=800&seq=villa-slopeview-ext&orientation=landscape",
    ],
    description:
      "Our flagship. Slopeview commands the valley with floor-to-ceiling glass and premium rates, making it the highest-revenue villa in our portfolio.",
    longDescription:
      "Slopeview is the crown jewel of the Nomad Livings collection. Positioned for maximum views and maximum yield, this premium glass pavilion delivers an unmatched guest experience — and charges accordingly. With the highest nightly tariff and strongest occupancy in the portfolio, it is built for investors seeking top-end appreciation and the best possible rental revenue per villa.",
  },
  {
    id: 5,
    slug: "crestline",
    type: "3 BHK Resort",
    name: "Crestline",
    price: "1.45 Cr",
    priceLabel: "₹1.45 Cr",
    roi: "38%",
    tag: "Resort",
    sqft: "1550 sq.ft",
    bedrooms: "3 Bed",
    guests: "8 Guests",
    featured: ["Resort-grade three-bedroom layout", "Private pool & lounge deck", "Built for high-occupancy hospitality clusters"],
    features: [
      "1550 sq.ft resort-grade three-bedroom layout",
      "Private pool and shaded lounge deck",
      "Built for boutique stays and high occupancy",
      "Premium hospitality fit-out throughout",
    ],
    gallery: [
      "https://readdy.ai/api/search-image?query=Large%20three%20bedroom%20A-frame%20resort%20villa%20with%20a%20private%20infinity%20pool%20and%20tropical%20garden%2C%20expansive%20deck%20and%20lounge%20area%2C%20lush%20greenery%2C%20golden%20hour%20warm%20light%2C%20luxury%20resort%20architecture%2C%20stylized%20artistic%20render%2C%20high%20detail&width=1200&height=800&seq=villa-crestline-01&orientation=landscape",
      INTERIOR_A,
      INTERIOR_B,
      "https://readdy.ai/api/search-image?query=Spacious%20resort-grade%20A-frame%20villa%20with%20outdoor%20pool%20deck%20and%20palm%20trees%2C%20warm%20evening%20lights%2C%20tropical%20boutique%20resort%20setting%2C%20stylized%20artistic%20render%2C%20high%20detail&width=1200&height=800&seq=villa-crestline-ext&orientation=landscape",
    ],
    description:
      "Our resort-grade three-bedroom villa with a private pool — engineered for boutique stays and high-occupancy hospitality clusters.",
    longDescription:
      "Crestline is built for investors targeting premium hospitality returns. The three-bedroom layout comfortably hosts families and groups, while the private pool and expansive lounge deck make it a standout on booking platforms and as a boutique resort anchor. With generous space and resort-grade amenities, it commands higher nightly tariffs and sustains the strong occupancy needed to deliver one of the best returns in the portfolio.",
  },
  {
    id: 6,
    slug: "ridgetop",
    type: "Dual Storey",
    name: "Ridgetop",
    price: "1.8 Cr",
    priceLabel: "₹1.8 Cr",
    roi: "40%",
    tag: "Flagship",
    sqft: "2100 sq.ft",
    bedrooms: "3 Bed",
    guests: "10 Guests",
    featured: ["Two-storey flagship design", "Rooftop viewing lounge", "Built as a flagship resort anchor"],
    features: [
      "2100 sq.ft two-storey flagship layout",
      "Rooftop viewing lounge with open terrace",
      "Built as a flagship resort anchor",
      "Unmatched panoramic views and space",
    ],
    gallery: [
      "https://readdy.ai/api/search-image?query=Grand%20two%20storey%20A-frame%20villa%20with%20a%20rooftop%20viewing%20lounge%20and%20open%20terrace%20perched%20on%20a%20scenic%20ridge%20line%20at%20sunset%2C%20panoramic%20mountain%20views%2C%20warm%20glowing%20lights%2C%20flagship%20luxury%20architecture%2C%20stylized%20artistic%20render%2C%20high%20detail&width=1200&height=800&seq=villa-ridgetop-01&orientation=landscape",
      INTERIOR_A,
      INTERIOR_B,
      "https://readdy.ai/api/search-image?query=Ultra-premium%20double%20storey%20A-frame%20villa%20on%20a%20hilltop%20ridge%20with%20rooftop%20lounge%20and%20glass%20railings%2C%20dramatic%20sunset%20sky%2C%20warm%20amber%20glow%2C%20flagship%20resort%20architecture%2C%20stylized%20artistic%20render%2C%20high%20detail&width=1200&height=800&seq=villa-ridgetop-ext&orientation=landscape",
    ],
    description:
      "Our flagship two-storey A-frame with a rooftop viewing lounge — built to anchor flagship resort clusters.",
    longDescription:
      "Ridgetop is the pinnacle of the Nomad Livings collection. As a two-storey flagship, it delivers the largest living footprint, a dedicated rooftop viewing lounge and sweeping panoramic views that command premium rates. Designed to anchor flagship resort clusters, it pairs outstanding guest experience with the highest revenue potential in the portfolio, making it the definitive choice for scale-focused investors.",
  },
];

export function getVillaModel(slug: string): VillaModel | undefined {
  return VILLA_MODELS.find((v) => v.slug === slug);
}